import React, { useEffect, useState } from "react";
import {
	Box,
	Typography,
	IconButton,
	TextField,
	InputAdornment,
	InputLabel,
	FormControl,
	Select,
	MenuItem,
} from "@mui/material";
import { ScraperConfigOption, ScraperConfigOptionValue } from "./types";
import AddIcon from "@mui/icons-material/Add";
import { useAdminPanelContext } from "../../context/AdminPanelContext";
import ConfigValueItem from "./ConfigValuesListItem";
import SearchIcon from "@mui/icons-material/Search";
import { globalBg } from "../../styles/theme";

interface ConfigValuesListProps {
	configOption: ScraperConfigOption;
	configId: number;
}

const ConfigValuesList: React.FC<ConfigValuesListProps> = ({
	configOption,
	configId,
}) => {
	const [lastUnsavedId, setLastUnsavedId] = useState<number>(0);
	const { handleConfigOptionValues } = useAdminPanelContext();
	const [searchQuery, setSearchQuery] = useState("");
	const [filteredValues, setFilteredValues] = useState(
		configOption.scraperConfigOptionValues || []
	);
	const [sortOrder, setSortOrder] = useState("asc");

	useEffect(() => {
		if (configOption) {
			const filtered = configOption.scraperConfigOptionValues
				.filter(
					(value) =>
						value.value.toLowerCase().includes(searchQuery.toLowerCase()) ||
						value.id === +searchQuery
				)
				.sort((a, b) =>
					sortOrder === "asc"
						? Date.parse(a.creationDatetime) - Date.parse(b.creationDatetime)
						: Date.parse(b.creationDatetime) - Date.parse(a.creationDatetime)
				);
			setFilteredValues(filtered);
		}
	}, [configOption, searchQuery, sortOrder]);

	const handleAddClick = () => {
		const newUnsavedId = lastUnsavedId - 1;
		const unsavedItem: ScraperConfigOptionValue = {
			id: newUnsavedId,
			active: true,
			value: `New ${configOption.name} ${-newUnsavedId}`,
		};
		setLastUnsavedId(newUnsavedId);
		handleConfigOptionValues.add(
			configId,
			configOption.name,
			unsavedItem,
			false
		);
	};

	const hasDuplicates = (optionValue: ScraperConfigOptionValue) => {
		if (
			configOption.scraperConfigOptionValues.some(
				(v) => v.id != optionValue.id && v.value === optionValue.value
			)
		) {
			console.error(
				`Cannot save due to duplicates in values. Duplicate value: ${optionValue.value}`
			);
			return true;
		}
		return false;
	};

	const handleValueDelete = (valueId: number) => {
		handleConfigOptionValues.delete(configId, configOption.name, valueId);
	};

	const handleSingleValueSave = async (
		optionValue: ScraperConfigOptionValue,
		updateActive = false
	) => {
		if (optionValue.id > 0 && updateActive) {
			await handleConfigOptionValues.update(
				configId,
				configOption.name,
				optionValue,
				updateActive
			);
			return { success: true, message: "" };
		}
		if (hasDuplicates(optionValue)) return { success: false };
		if (optionValue.id > 0) {
			await handleConfigOptionValues.update(
				configId,
				configOption.name,
				optionValue
			);
			return { success: true, message: "" };
		}
		await handleConfigOptionValues.add(
			configId,
			configOption.name,
			optionValue
		);
		return { success: true, message: "" };
	};

	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<Typography variant="h5" component="div" textAlign="center">
				Searchterms
				<IconButton color="primary" onClick={handleAddClick}>
					<AddIcon />
				</IconButton>
			</Typography>
			<Box sx={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
				<TextField
					id="outlined-search"
					label="Search"
					type="search"
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
					size="small"
					sx={{ flexGrow: 1 }}
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								<SearchIcon />
							</InputAdornment>
						),
					}}
				/>
				<FormControl sx={{ width: 140 }} size="small">
					<InputLabel id="sort-select-label">Sort by</InputLabel>
					<Select
						labelId="sort-select-label"
						id="sort-select"
						value={sortOrder}
						onChange={(e) => setSortOrder(e.target.value as string)}
						label="Sort by"
					>
						<MenuItem value="asc">Oldest first</MenuItem>
						<MenuItem value="desc">Newest first</MenuItem>
					</Select>
				</FormControl>
			</Box>
			<Typography
				sx={{
					color: "gray",
					marginLeft: "14px",
					fontSize: "16px",
					marginBottom: "15px",
				}}
			>
				Search term
			</Typography>
			<Box
				sx={{
					flexGrow: 1,
					display: "flex",
					flexDirection: "column",
					gap: "5px",
					height: "100%",
					overflowY: "auto",
					backgroundColor: globalBg,
					paddingBottom: "20px",
					paddingRight: "5px",
				}}
			>
				{filteredValues?.length
					? filteredValues.map((value) => {
							return (
								<ConfigValueItem
									key={value.value + value.id}
									configValue={value}
									handleDelete={handleValueDelete}
									handleSave={handleSingleValueSave}
								></ConfigValueItem>
							);
					  })
					: null}
			</Box>
		</Box>
	);
};

export default ConfigValuesList;
